import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useState, useEffect, useRef } from "react";

// Import core architecture components
import Home from "./components/Home";
import About from "./components/About";
import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import ProductsPage from "./components/Productspage";
import QualityPage from "./components/Qualitypage";
import Contact from "./components/Contact";
import WhatsAppButton from "./pages/WhatsAppButton";
import ScrollTopButton from "./pages/ScrollTopButton";

// Custom hook for delayed navigation with scroll to top
function useDelayedNavigation() {
  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const pendingNavigation = useRef(null);

  const delayedNavigate = (path, delay = 800) => {
    if (isTransitioning) return;

    // Start transition
    setIsTransitioning(true);

    // Store the navigation to execute after delay
    pendingNavigation.current = setTimeout(() => {
      navigate(path);
      setIsTransitioning(false);
      pendingNavigation.current = null;
    }, delay);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pendingNavigation.current) {
        clearTimeout(pendingNavigation.current);
      }
    };
  }, []);

  return { delayedNavigate, isTransitioning };
}

// Scroll to Top Component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top smoothly when route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [pathname]);

  return null;
}

// Page Transition Component
function PageTransition({ children }) {
  const location = useLocation();
  const { delayedNavigate, isTransitioning } = useDelayedNavigation();
  const [prevLocation, setPrevLocation] = useState(location);

  // Intercept all navigation attempts
  useEffect(() => {
    // Override Link clicks
    const handleLinkClick = (e) => {
      const link = e.target.closest("a");
      if (link && link.href && link.href.startsWith(window.location.origin)) {
        const path = link.pathname;
        if (path !== location.pathname) {
          e.preventDefault();
          delayedNavigate(path);
        }
      }
    };

    document.addEventListener("click", handleLinkClick);

    return () => {
      document.removeEventListener("click", handleLinkClick);
    };
  }, [location.pathname, delayedNavigate]);

  // Update when location changes (after navigation)
  useEffect(() => {
    if (location !== prevLocation) {
      setPrevLocation(location);
    }
  }, [location, prevLocation]);

  return (
    <>
      {/* Page Transition Overlay */}
      <div className={`transition-wrapper ${isTransitioning ? "active" : ""}`}>
        <div className="layer layer-black"></div>
        <div className="layer layer-gold"></div>
        <div className="layer layer-white"></div>
      </div>

      {/* Render page content */}
      <div
        className={`page-content ${isTransitioning ? "page-exit" : "page-enter"}`}
      >
        {children}
      </div>
    </>
  );
}

// Modified Navbar component wrapper (if you need to handle programmatic navigation)
function AppContent() {
  return (
    <div className="App">
      <Navbar />
      <main className="emk-main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/products" element={<ProductsPage />} />
          {/* <Route path="/quality" element={<QualityPage />} /> */}
          <Route path="/contact" element={<Contact />} />
        </Routes>
      </main>
      <Footer />
      <ScrollTopButton />
      <WhatsAppButton />
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop /> {/* This ensures scroll to top on route change */}
      <PageTransition>
        <AppContent />
      </PageTransition>
    </Router>
  );
}

export default App;
